// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { initAuth, getCurrentUser, logout as apiLogout } from '../services/api/auth';
import { getUserOrganizations } from '../services/api/organizations';
import { setOrgScope, clearAll as clearCache } from '../services/api/cache';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [currentOrganization, setCurrentOrganization] = useState(null);
    const [organizations, setOrganizations] = useState([]);
    const navigate = useNavigate();

    // Try to restore organization from localStorage on mount
    useEffect(() => {
        const savedOrg = localStorage.getItem('currentOrganization');
        if (savedOrg) {
            try {
                const orgData = JSON.parse(savedOrg);
                setCurrentOrganization(orgData);
            } catch (error) {
                console.error('Error parsing saved organization:', error);
            }
        }
    }, []);

    const refreshOrganizations = async () => {
        try {
            const orgs = await getUserOrganizations();
            setOrganizations(orgs);
        } catch (error) {
            console.error('Error fetching organizations:', error);
        }
    };

    useEffect(() => {
        const initializeAuth = async () => {
            setIsLoading(true);

            // Check for token in localStorage
            const hasToken = initAuth();

            if (hasToken) {
                try {
                    // Get current user data
                    const userData = await getCurrentUser();
                    setUser(userData);
                    setIsAuthenticated(true);

                    // Fetch organizations for page refresh scenario
                    try {
                        const orgs = await getUserOrganizations();
                        setOrganizations(orgs);

                        // If user has a saved organization, verify it's still valid
                        const savedOrgId = localStorage.getItem('lastSelectedOrganizationId');
                        if (savedOrgId && orgs.length > 0) {
                            const savedOrg = orgs.find(org => org.id === parseInt(savedOrgId));
                            if (savedOrg && !currentOrganization) {
                                selectOrganization(savedOrg);
                            }
                        }
                    } catch (orgError) {
                        console.error('Error fetching organizations:', orgError);
                    }
                } catch (error) {
                    console.error('Error initializing auth:', error);
                    handleLogout();
                }
            }

            setIsLoading(false);
        };

        initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleLogout = () => {
        apiLogout();
        clearCache();
        setUser(null);
        setIsAuthenticated(false);
        setCurrentOrganization(null);
        setOrganizations([]);
        // Keep lastSelectedOrganizationId in localStorage for next login
        navigate('/login');
    };

    const selectOrganization = (organization) => {
        // Store organization ID and timestamp for smart defaults
        localStorage.setItem('lastSelectedOrganizationId', organization.id.toString());
        localStorage.setItem('selectedOrganizationId', organization.id.toString());
        localStorage.setItem('lastOrganizationSelectedAt', new Date().toISOString());

        // Keep the full organization object for backward compatibility
        localStorage.setItem('currentOrganization', JSON.stringify(organization));

        // Clear API cache when switching organizations
        setOrgScope(organization.id);

        setCurrentOrganization(organization);
    };

    const handleLogin = async (userData, { redirectTo } = {}) => {
        setUser(userData);
        setIsAuthenticated(true);

        // Fetch organizations after login
        try {
            const orgs = await getUserOrganizations();
            setOrganizations(orgs);

            if (orgs && orgs.length > 0) {
                // Try to restore last selected organization
                const lastSelectedOrgId = localStorage.getItem('lastSelectedOrganizationId');
                let selectedOrg = null;

                if (lastSelectedOrgId) {
                    selectedOrg = orgs.find(org => org.id === parseInt(lastSelectedOrgId));
                }

                // If no valid last selection, auto-select the first available organization
                if (!selectedOrg) {
                    selectedOrg = orgs[0];
                }

                // Set the selected organization and navigate
                if (selectedOrg) {
                    selectOrganization(selectedOrg);
                    navigate(redirectTo || '/');
                    return;
                }
            }

            // No organizations available, show onboarding
            navigate('/onboarding');

        } catch (error) {
            console.error('Error fetching organizations in handleLogin:', error);
            navigate('/onboarding');
        }
    };

    const value = {
        user,
        setUser,
        isAuthenticated,
        setIsAuthenticated,
        isLoading,
        logout: handleLogout,
        currentOrganization,
        selectOrganization,
        organizations,
        refreshOrganizations,
        handleLogin
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
